// Org context store — fetches orgs the current user belongs to via RLS.
// Unlike owner-scoped stores, org data is multi-user; RLS on the DB
// side filters to orgs where the caller is an active member.
//
// Pattern: no local IDB caching (orgs change rarely and are small).
// Fetches on sign-in and on explicit refresh.

import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { useAuthStore } from './authStore';

export type OrgRole =
  | 'owner'
  | 'chief_pilot'
  | 'director_of_ops'
  | 'do'
  | 'pic'
  | 'sic'
  | 'crew'
  | 'mechanic'
  | 'admin'
  | 'observer';

// Numeric weights for role hierarchy (higher = more authority)
const ROLE_WEIGHT: Record<OrgRole, number> = {
  owner: 100,
  chief_pilot: 80,
  director_of_ops: 80,
  do: 80,
  admin: 60,
  pic: 40,
  sic: 30,
  mechanic: 20,
  crew: 10,
  observer: 0,
};

/** Returns true if roleA has at least as much authority as roleB */
export function roleAtLeast(roleA: OrgRole | null, roleB: OrgRole): boolean {
  if (!roleA) return false;
  return (ROLE_WEIGHT[roleA] ?? 0) >= (ROLE_WEIGHT[roleB] ?? 0);
}

export interface Organization {
  id: string;
  name: string;
  legal_name: string | null;
  // Aviation regulatory
  faa_certificate_number: string | null;
  ops_specs_section: string | null;
  ops_base_airport_ident: string | null;
  ops_manual_url: string | null;
  // Federal contractor identifiers
  uei: string | null;
  cage_code: string | null;
  ein: string | null;
  duns: string | null;
  naics_code: string | null;
  // Mailing address
  mailing_address_line1: string | null;
  mailing_address_line2: string | null;
  mailing_city: string | null;
  mailing_state: string | null;
  mailing_postal_code: string | null;
  mailing_country: string | null;
  // Billing address (when null — use mailing)
  billing_address_line1: string | null;
  billing_address_line2: string | null;
  billing_city: string | null;
  billing_state: string | null;
  billing_postal_code: string | null;
  billing_country: string | null;
  // Contact + web
  contact_email: string | null;
  contact_phone: string | null;
  fax_phone: string | null;
  website: string | null;
  // Misc / freeform settings blob (kept for future extension)
  settings: Record<string, unknown>;
  /** Days to retain audit events + briefing snapshots. Default 1095
   *  (3 years, FAA Part 135). Federal contract operators often raise
   *  to 7 years (2555 days). Settable in Organization Settings. */
  compliance_retention_days: number;
  created_at: string;
  updated_at: string;
}

/** Editable subset of Organization fields. Excludes server-managed
 *  metadata (id, created_at, updated_at) and the freeform settings
 *  blob (which has its own future-proof editor). */
export type OrganizationPatch = Partial<Omit<Organization,
  'id' | 'created_at' | 'updated_at' | 'settings'
>>;

export interface OrgMember {
  id: string;
  org_id: string;
  user_id: string;
  role: OrgRole;
  invited_email: string | null;
  status: string;
  created_at: string;
}

interface OrgState {
  orgs: Organization[];
  membership: OrgMember[];    // all memberships for the current user
  activeOrgId: string | null; // which org is "in focus" for FRAT etc.
  loading: boolean;
  error: string | null;

  fetchOrgs: () => Promise<void>;
  setActiveOrg: (orgId: string) => void;
  activeOrg: () => Organization | null;
  myRoleIn: (orgId: string) => OrgRole | null;
  canApproveIn: (orgId: string) => boolean;
  createOrg: (
    name: string,
    opts?: Partial<Omit<Organization, 'id' | 'created_at' | 'updated_at'>>,
  ) => Promise<Organization | null>;
  /** Update fields on an existing org. Optimistically applies the
   *  patch to local state so the form feels instant, then pushes to
   *  Supabase. On server failure, refreshes from server so the UI
   *  reflects the truthy state. */
  updateOrganization: (
    orgId: string,
    patch: OrganizationPatch,
  ) => Promise<{ ok: boolean; error?: string }>;
}

// Module-scoped in-flight dedup. Two callers (orgStore subscriber +
// accountContextStore.hydrate()) fire fetchOrgs() in parallel on
// sign-in; without this they each hit Supabase.
let inFlightFetchOrgs: Promise<void> | null = null;

export const useOrgStore = create<OrgState>((set, get) => ({
  orgs: [],
  membership: [],
  activeOrgId: null,
  loading: false,
  error: null,

  fetchOrgs: async () => {
    if (inFlightFetchOrgs) return inFlightFetchOrgs;
    if (!supabase) return;
    const user = useAuthStore.getState().user;
    if (!user) return;
    set({ loading: true, error: null });
    const work = (async () => {
    try {
      // Fetch this user's memberships first (RLS: user_id = auth.uid())
      const { data: memberships, error: mErr } = await supabase
        .from('org_members')
        .select('*')
        .eq('user_id', user.id)
        .eq('status', 'active');
      if (mErr) throw mErr;

      const orgIds = (memberships ?? []).map((m) => m.org_id as string);
      if (orgIds.length === 0) {
        set({ orgs: [], membership: memberships ?? [], loading: false });
        return;
      }

      // Fetch orgs (RLS: is_org_member lets the user see their orgs)
      const { data: orgs, error: oErr } = await supabase
        .from('organizations')
        .select('*')
        .in('id', orgIds);
      if (oErr) throw oErr;

      const orgList = (orgs ?? []) as Organization[];
      set({
        orgs: orgList,
        membership: (memberships ?? []) as OrgMember[],
        // Keep activeOrgId if still valid; otherwise default to first org
        activeOrgId: orgList.find((o) => o.id === get().activeOrgId)?.id
          ?? orgList[0]?.id
          ?? null,
        loading: false,
      });
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      set({ loading: false, error: msg });
    }
    })().finally(() => { inFlightFetchOrgs = null; });
    inFlightFetchOrgs = work;
    return work;
  },

  setActiveOrg: (orgId) => set({ activeOrgId: orgId }),

  activeOrg: () => {
    const { orgs, activeOrgId } = get();
    return orgs.find((o) => o.id === activeOrgId) ?? null;
  },

  myRoleIn: (orgId) => {
    const m = get().membership.find((mem) => mem.org_id === orgId);
    return m ? (m.role as OrgRole) : null;
  },

  canApproveIn: (orgId) => {
    const role = get().myRoleIn(orgId);
    return roleAtLeast(role, 'chief_pilot');
  },

  createOrg: async (name, opts) => {
    if (!supabase) return null;
    const user = useAuthStore.getState().user;
    if (!user) return null;
    try {
      const { data: org, error: orgErr } = await supabase
        .from('organizations')
        .insert({ name, ...(opts ?? {}), settings: opts?.settings ?? {} })
        .select()
        .single();
      if (orgErr) throw orgErr;

      // Auto-enroll creator as owner
      const { error: memErr } = await supabase
        .from('org_members')
        .insert({ org_id: org.id, user_id: user.id, role: 'owner', status: 'active' });
      if (memErr) throw memErr;

      // Refresh local state
      await get().fetchOrgs();
      return org as Organization;
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      set({ error: msg });
      return null;
    }
  },

  updateOrganization: async (orgId, patch) => {
    if (!supabase) return { ok: false, error: 'supabase not configured' };
    // Optimistically merge into local state so the form feels instant.
    const prev = get().orgs;
    const optimistic = prev.map((o) => (o.id === orgId ? { ...o, ...patch } : o));
    set({ orgs: optimistic });
    try {
      // ⚠️ IMPORTANT: append .select() so PostgREST returns the rows it
      // actually wrote. Without .select(), an RLS-filtered update that
      // matches 0 rows silently returns `{ data: null, error: null }`
      // and the caller assumes success — the bug Boss hit on
      // 2026-05-12 where every org-settings edit reverted on refresh.
      const { data, error, status } = await supabase
        .from('organizations')
        .update(patch)
        .eq('id', orgId)
        .select();
      if (error) {
        // eslint-disable-next-line no-console
        console.error('[orgStore] updateOrganization failed:', status, error.message);
        await get().fetchOrgs();
        return { ok: false, error: error.message };
      }
      if (!data || data.length === 0) {
        // RLS silently filtered the row. Surface this as an error so
        // the UI can show a save-failed indicator instead of pretending
        // it worked.
        const msg =
          'Update wrote 0 rows — your account may not have admin permission on this organization, ' +
          'or your session expired. Try signing out and back in.';
        // eslint-disable-next-line no-console
        console.error('[orgStore] updateOrganization silent no-op:', msg);
        await get().fetchOrgs();
        return { ok: false, error: msg };
      }
      // Reconcile local state from the authoritative server row so any
      // server-side computed fields (updated_at, etc.) stay accurate.
      const serverRow = data[0] as Organization;
      set({ orgs: get().orgs.map((o) => (o.id === orgId ? serverRow : o)) });
      return { ok: true };
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      // eslint-disable-next-line no-console
      console.error('[orgStore] updateOrganization threw:', msg);
      await get().fetchOrgs();
      return { ok: false, error: msg };
    }
  },
}));

// Auto-fetch when user signs in
useAuthStore.subscribe((state, prev) => {
  if (!prev.user && !!state.user) {
    void useOrgStore.getState().fetchOrgs();
  }
});
